import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import {
  useApplyTicketRewardMutation,
  useLazyGetLotteryInfoQuery,
} from '@/api/queries/lottery/lottery';
import { productOfDayRewardsConfig } from '@/common/config/product-of-day-config';
import { parseLotteryTicket } from '@/common/lib/parseLotteryTicket';
import { type LotteryRewardName } from '@/common/types/lottery';
import {
  H1,
  StringMain,
  StringXSmall,
} from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { showToast } from '@/components/ui/Notification/SwipeableToastNotification';
import { Picture } from '@/components/ui/Picture/Picture';
import { TicketIcon } from '@/components/ui/TicketIcon/TicketIcon';
import { changeClaimLotteryRewardsStatus } from '@/redux/reducers/lotteryReducer';
import { addToBalance } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import { LotteryBgWrapper } from '../LotteryBgWrapper/LotteryBgWrapper';

import { rewardsImage } from './config';

import css from './styles.module.scss';

import blurredLight from '/img/blurred-light.png';

const ClaimLotteryRewards: FC = () => {
  const unclaimedRewards = useAppSelector(
    (state) => state.lottery.myUnclaimedTickets,
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const dispatch = useAppDispatch();
  const [applyReward, { isLoading }] = useApplyTicketRewardMutation();
  const [withTicketReward, setWithTicketReward] = useState(false);
  const [getLotteryInfo, lotteryInfoApiState] = useLazyGetLotteryInfoQuery();
  const { t } = useTranslation();

  const currentReward = unclaimedRewards[currentIndex];

  const rewardName = currentReward
    ? (Object.keys(currentReward.drawResult)[0] as LotteryRewardName)
    : undefined;

  const images = rewardName ? rewardsImage[rewardName] : undefined;

  const claimReward = async (): Promise<void> => {
    if (!currentReward) {
      return;
    }

    try {
      const ticketId = currentReward.id;
      const ticketRewardResponse = await applyReward({ ticketId }).unwrap();
      const rewardName = Object.keys(ticketRewardResponse.drawResult)[0];

      if (rewardName === 'usdt' || rewardName === 'tgem') {
        dispatch(addToBalance(ticketRewardResponse.drawResult));
      } else if (rewardName !== 'dice') {
        // Tickets: the lottery info is reloaded after the last claim. Dice
        // land in the wallet on the server, the dice game reads it there.
        setWithTicketReward(true);
      }

      if (currentIndex < unclaimedRewards.length - 1) {
        setCurrentIndex((state) => state + 1);

        return;
      }

      if (withTicketReward) {
        await getLotteryInfo().unwrap();
      }
      dispatch(changeClaimLotteryRewardsStatus(false));
      setWithTicketReward(false);
    } catch (error) {
      console.error(error);
      showToast('Something went wrong, please try again');
    }
  };

  const btnText =
    unclaimedRewards.length > 1
      ? ` ${currentIndex + 1}/${unclaimedRewards.length}`
      : '';
  const currentTicketType = currentReward
    ? parseLotteryTicket(currentReward.ticket).type
    : undefined;

  const ticketsRewars = ['silverTicket', 'goldTicket', 'diamondTicket'];

  return (
    currentReward &&
    currentTicketType && (
      <LotteryBgWrapper className="h-full" type={currentTicketType}>
        <div className="flex h-full w-full flex-col items-center px-6 pb-[4.5rem] pt-36">
          <H1 weight="extrabold" className="mb-auto relative z-[1]">
            {t('Congrats!')}
          </H1>
          <div className="relative size-[144px] mt-[80px] md-h:mt-[30px]">
            {images && (
              <>
                <Picture
                  {...images.light}
                  className="absolute w-[var(--desktop-container-content-width)] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                />
                <Picture
                  className={cn(
                    css.lightAnimation,
                    rewardName && ticketsRewars.includes(rewardName as string)
                      ? 'mix-blend-luminosity'
                      : 'mix-blend-plus-lighter',
                  )}
                  img={blurredLight}
                  alt="light"
                />
                <Picture
                  className="relative z-[1] size-full"
                  {...images.image}
                />
              </>
            )}
          </div>
          {rewardName && (
            <span className="relative text-[40px] z-[1] font-extrabold !leading-none">
              {currentReward.drawResult[rewardName]}
            </span>
          )}
          {rewardName && (
            <StringMain
              weight="medium"
              className="!text-white/60 !leading-none mb-[5.18vh]"
            >
              {
                productOfDayRewardsConfig[
                  rewardName as keyof typeof productOfDayRewardsConfig
                ].label
              }
            </StringMain>
          )}
          {currentReward.drawRank !== null && (
            <div className="flex flex-col gap-2 items-center">
              <TicketIcon type={currentTicketType} className="size-[40px]" />
              <StringMain weight="bold" className="tracking-[3px]">
                {currentReward.ticket}
              </StringMain>
              <StringXSmall className="!text-white/60">
                {t('Winning ticket')}
              </StringXSmall>
            </div>
          )}
          <MainButton
            className="mt-auto"
            isLoading={isLoading || lotteryInfoApiState.isLoading}
            disabled={isLoading || lotteryInfoApiState.isLoading}
            onClick={claimReward}
            text={'Claim' + btnText}
          />
        </div>
      </LotteryBgWrapper>
    )
  );
};

export { ClaimLotteryRewards };
